/**
 * AcceptInvitePage - entry point for org invite links (`/invite/:token`).
 *
 * State machine (see README for the full diagram):
 *   loading
 *     -> terminal (expired | revoked | accepted)
 *     -> pre-login (unauthed + pending)
 *     -> accepting
 *          -> success (refresh org, switch active, navigate /admin)
 *          -> mismatch (403 invite_email_mismatch)
 *          -> terminal (410 invite_expired | invite_revoked | invite_accepted)
 *          -> error (retryable)
 *     -> error (GET failed, retryable)
 *
 * Trust note: the page never sends the user_id/email to the backend; the
 * server extracts them from the JWT. The `next` param for /login is
 * allow-listed by the LoginPage before being used as a redirect target.
 *
 * @module components/invite/AcceptInvitePage
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSupabase } from '../../lib/SupabaseProvider';
import { useOrg } from '../../lib/OrgProvider';
import { captureException, captureEvent } from '../../lib/telemetry/posthog/frontend';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Server-side invite status values as returned by GET /api/invites/:token. */
type InviteStatus = 'pending' | 'expired' | 'revoked' | 'accepted';

/** Masked invite metadata returned by the public GET endpoint. */
interface InviteInfo {
  org_name: string;
  inviter_name: string;
  expires_at: string;
  email_masked: string;
  status: InviteStatus;
}

/** UI states derived from the fetch result and the accept-call outcome. */
type UiState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; action: 'retry' | 'none' }
  | { kind: 'uncertain'; info: InviteInfo }
  | { kind: 'checkError'; info: InviteInfo }
  | { kind: 'pendingAfterCheck'; info: InviteInfo }
  | { kind: 'reauth'; info: InviteInfo; actionError?: string }
  | { kind: 'terminal'; status: 'expired' | 'revoked' | 'accepted'; info?: InviteInfo }
  | { kind: 'preLogin'; info: InviteInfo }
  | { kind: 'accepting'; info: InviteInfo }
  | {
      kind: 'mismatch';
      info: InviteInfo;
      /** Masked email from the 403 body; falls back to the raw GET value. */
      invitedEmailMasked: string;
      /** Currently signed-in user's email (used in the mismatch copy). */
      signedInEmail: string | null;
      /** Safe recovery copy when the sign-out action fails. */
      actionError?: string;
    };

// ---------------------------------------------------------------------------
// Styles (match existing primitives - buttons rounded-lg, cards rounded-xl)
// ---------------------------------------------------------------------------

const CARD_CLASSES = 'w-full max-w-md rounded-xl bg-background border border-border p-8';
const PRIMARY_BUTTON_CLASSES =
  'inline-flex items-center justify-center rounded-lg px-4 py-2.5 button-accent font-medium transition-colors disabled:bg-surface-raised disabled:text-muted-foreground disabled:cursor-not-allowed';
const SECONDARY_BUTTON_CLASSES =
  'inline-flex items-center justify-center rounded-lg px-4 py-2.5 bg-surface text-muted-foreground font-medium hover:bg-surface-hover transition-colors';

const MAX_ORG_NAME_LENGTH = 200;
const MAX_INVITER_NAME_LENGTH = 200;
const MAX_EXPIRES_AT_LENGTH = 64;
const MAX_MASKED_EMAIL_LENGTH = 320;
const MAX_ROLE_NAME_LENGTH = 100;
const SIGN_OUT_ERROR = "We couldn't sign you out. Try again.";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Human-readable formatter for expires_at. */
function formatExpiry(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Terminal copy per known non-pending status. */
function terminalCopy(status: 'expired' | 'revoked'): string {
  if (status === 'expired') return 'Ask the person who shared it for a new link.';
  return 'Ask the person who shared it for a new link.';
}

/** Focused heading for a known terminal invite outcome. */
function terminalHeading(status: 'expired' | 'revoked' | 'accepted'): string {
  if (status === 'expired') return 'This invite has expired';
  if (status === 'revoked') return 'This invite was cancelled';
  return 'This invite has already been accepted';
}

/** Map a 410 error code back to its terminal status. */
function errorCodeToTerminal(code: string | undefined): 'expired' | 'revoked' | 'accepted' | null {
  if (code === 'invite_expired') return 'expired';
  if (code === 'invite_revoked') return 'revoked';
  if (code === 'invite_accepted') return 'accepted';
  return null;
}

/**
 * Extract the error code from the accept-response body.
 *
 * metering-svc raises `HTTPException(detail={error, detail})`, which FastAPI's
 * default handler serialises as `{"detail": {"error": ..., "detail": ...}}`.
 * Early test fixtures hand-rolled the unwrapped `{"error": ...}` shape so the
 * parser also accepts that - both paths keep the mismatch/terminal branches
 * reachable.
 */
function extractErrorCode(body: unknown): string | undefined {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return undefined;
  const record = body as Record<string, unknown>;
  if (typeof record.detail === 'object' && record.detail !== null && !Array.isArray(record.detail)) {
    const detail = record.detail as Record<string, unknown>;
    if (typeof detail.error === 'string') return detail.error;
  }
  return typeof record.error === 'string' ? record.error : undefined;
}

/** True when a contract string is non-empty and within its display bound. */
function isBoundedString(value: unknown, maxLength: number): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= maxLength;
}

function isInviteStatus(value: unknown): value is InviteStatus {
  return value === 'pending'
    || value === 'expired'
    || value === 'revoked'
    || value === 'accepted';
}

function isExpiry(value: unknown): value is string {
  return isBoundedString(value, MAX_EXPIRES_AT_LENGTH)
    && Number.isFinite(Date.parse(value));
}

function isMaskedEmail(value: unknown): value is string {
  return isBoundedString(value, MAX_MASKED_EMAIL_LENGTH)
    && /^[^@\s*]?\*+@[^@\s]+$/.test(value);
}

/** True when a value uses PostgreSQL's canonical UUID text shape. */
function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

/** Independently validate the public GET response before using it in UI. */
function parseInviteInfo(value: unknown): InviteInfo | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const body = value as Record<string, unknown>;

  if (
    !isBoundedString(body.org_name, MAX_ORG_NAME_LENGTH)
    || !isBoundedString(body.inviter_name, MAX_INVITER_NAME_LENGTH)
    || !isExpiry(body.expires_at)
    || !isMaskedEmail(body.email_masked)
    || !isInviteStatus(body.status)
  ) {
    return null;
  }

  return {
    org_name: body.org_name,
    inviter_name: body.inviter_name,
    expires_at: body.expires_at,
    email_masked: body.email_masked,
    status: body.status,
  };
}

/** Independently validate the accept success response before changing org. */
function parseAcceptSuccess(value: unknown): { organization_id: string; role_name: string } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const body = value as Record<string, unknown>;
  if (
    !isUuid(body.organization_id)
    || !isBoundedString(body.role_name, MAX_ROLE_NAME_LENGTH)
  ) {
    return null;
  }
  return { organization_id: body.organization_id, role_name: body.role_name };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Renders the invite-acceptance flow. Handles loading, pre-login, auto-accept,
 * terminal, mismatch and error states.
 */
export const AcceptInvitePage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { session, signOut, supabase } = useSupabase();
  const { refresh, setActiveOrg } = useOrg();

  /** Email of the currently signed-in user, if any. Rendered in the
   *  mismatch copy so users can see which account is mis-matched. */
  const signedInEmail = session?.user?.email ?? null;

  const [state, setState] = useState<UiState>({ kind: 'loading' });
  const blockingHeadingRef = useRef<HTMLHeadingElement>(null);

  const loginHref = useMemo(
    () => `/login?next=${encodeURIComponent(`/invite/${token}`)}`,
    [token],
  );
  const adminLoginHref = `/login?next=${encodeURIComponent('/admin')}`;

  // Guard against double-accept on re-renders (StrictMode + re-run effect).
  const acceptAttemptedRef = useRef(false);
  const loadAttemptedRef = useRef(false);

  const loadInvite = useCallback(async (checkInfo?: InviteInfo): Promise<InviteInfo | null> => {
    const setRetryableLoadError = () => {
      if (checkInfo) {
        setState({ kind: 'checkError', info: checkInfo });
        return;
      }
      setState({
        kind: 'error',
        message: 'Try opening the invite again.',
        action: 'retry',
      });
    };
    try {
      const res = await fetch(`/api/invites/${encodeURIComponent(token)}`, {
        cache: 'no-store',
      });
      if (!res.ok) {
        if (res.status === 404) {
          setState({
            kind: 'error',
            message: 'Ask the person who shared it for a new link.',
            action: 'none',
          });
          return null;
        }
        setRetryableLoadError();
        return null;
      }
      const info = parseInviteInfo(await res.json());
      if (!info) {
        captureException(
          new Error('Invite GET returned an invalid public contract'),
          'AcceptInvitePage.loadInvite',
          {},
        );
        setRetryableLoadError();
        return null;
      }

      // Non-pending statuses -> terminal.
      if (info.status !== 'pending') {
        setState({ kind: 'terminal', status: info.status as 'expired' | 'revoked' | 'accepted', info });
        return null;
      }

      return info;
    } catch {
      captureException(
        new Error('Invite GET request failed'),
        'AcceptInvitePage.loadInvite',
        {},
      );
      setRetryableLoadError();
      return null;
    }
  }, [token]);

  const acceptInvite = useCallback(
    async (info: InviteInfo, accessToken: string) => {
      try {
        const res = await fetch(`/api/invites/${encodeURIComponent(token)}/accept`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          // Non-JSON is outside the browser's accepted contract.
        }

        if (res.status === 401) {
          setState({ kind: 'reauth', info });
          return;
        }

        const errorCode = extractErrorCode(body);
        const success = res.status === 200 ? parseAcceptSuccess(body) : null;

        if (success) {
          captureEvent('invite_accept_success', { orgId: success.organization_id });
          try {
            refresh();
            setActiveOrg(success.organization_id);
          } catch {
            captureException(
              new Error('Invite post-accept organization update failed'),
              'AcceptInvitePage.postAccept',
              { orgId: success.organization_id },
            );
          }
          void navigate('/admin', { replace: true });
          return;
        }

        if (res.status === 403 && errorCode === 'invite_email_mismatch') {
          // Masked email comes from the public GET only - metering-svc does
          // not echo it on 403 and we don't widen our contract to accept it.
          setState({
            kind: 'mismatch',
            info,
            invitedEmailMasked: info.email_masked,
            signedInEmail,
          });
          return;
        }

        if (res.status === 410) {
          const terminal = errorCodeToTerminal(errorCode);
          if (terminal) {
            setState({ kind: 'terminal', status: terminal, info });
            return;
          }
        }

        setState({ kind: 'uncertain', info });
      } catch {
        captureException(
          new Error('Invite accept request failed'),
          'AcceptInvitePage.acceptInvite',
          {},
        );
        setState({ kind: 'uncertain', info });
      }
    },
    [token, refresh, setActiveOrg, navigate, signedInEmail],
  );

  const runFlow = useCallback(async () => {
    loadAttemptedRef.current = true;
    const info = await loadInvite();
    if (!info) return;

    if (!session?.access_token) {
      setState({ kind: 'preLogin', info });
      return;
    }

    if (acceptAttemptedRef.current) return;
    acceptAttemptedRef.current = true;
    setState({ kind: 'accepting', info });
    await acceptInvite(info, session.access_token);
  }, [loadInvite, session?.access_token, acceptInvite]);

  useEffect(() => {
    if (loadAttemptedRef.current) return;
    void runFlow();
  }, [runFlow]);

  useEffect(() => {
    if (
      state.kind === 'error'
      || state.kind === 'uncertain'
      || state.kind === 'checkError'
      || state.kind === 'pendingAfterCheck'
      || state.kind === 'terminal'
      || state.kind === 'mismatch'
      || state.kind === 'reauth'
    ) {
      blockingHeadingRef.current?.focus();
    }
  }, [state]);

  const handleRetry = useCallback(() => {
    loadAttemptedRef.current = false;
    acceptAttemptedRef.current = false;
    setState({ kind: 'loading' });
    void runFlow();
  }, [runFlow]);

  const handleCheckStatus = useCallback(async () => {
    if (state.kind !== 'uncertain' && state.kind !== 'checkError') return;
    const previousInfo = state.info;
    setState({ kind: 'loading' });
    const info = await loadInvite(previousInfo);
    if (info) {
      setState({ kind: 'pendingAfterCheck', info });
    }
  }, [state, loadInvite]);

  const handleTryAcceptAgain = useCallback(async () => {
    if (state.kind !== 'pendingAfterCheck') return;
    if (!session?.access_token) {
      setState({ kind: 'preLogin', info: state.info });
      return;
    }
    acceptAttemptedRef.current = true;
    setState({ kind: 'accepting', info: state.info });
    await acceptInvite(state.info, session.access_token);
  }, [state, session?.access_token, acceptInvite]);

  const performRecoverySignOut = useCallback(async () => {
    if (supabase?.auth?.signOut) {
      const { error } = await supabase.auth.signOut();
      if (error) throw new Error('Sign-out failed');
      return;
    }
    await signOut();
  }, [supabase, signOut]);

  const handleReauthenticate = useCallback(async () => {
    try {
      await performRecoverySignOut();
    } catch {
      captureException(
        new Error('Invite reauthentication sign-out failed'),
        'AcceptInvitePage.reauthenticate',
        {},
      );
      setState((current) => current.kind === 'reauth'
        ? { ...current, actionError: SIGN_OUT_ERROR }
        : current);
      return;
    }
    void navigate(loginHref, { replace: true });
  }, [performRecoverySignOut, navigate, loginHref]);

  const handleSignOutAndRetry = useCallback(async () => {
    // Capture the invite info BEFORE sign-out so we render preLogin directly
    // instead of funnelling through runFlow, whose closure still holds the
    // stale session and would re-fire an auto-accept → trap the user in a
    // mismatch loop. Also keeps the network quiet: no redundant GET invite.
    const cachedInfo = state.kind === 'mismatch' ? state.info : null;
    try {
      await performRecoverySignOut();
    } catch {
      captureException(
        new Error('Invite mismatch sign-out failed'),
        'AcceptInvitePage.signOut',
        {},
      );
      setState((current) => current.kind === 'mismatch'
        ? { ...current, actionError: SIGN_OUT_ERROR }
        : current);
      return;
    }
    // Suppress the on-mount effect from re-running after session change:
    // we already know where we want to land.
    loadAttemptedRef.current = true;
    acceptAttemptedRef.current = false;
    if (cachedInfo) {
      setState({ kind: 'preLogin', info: cachedInfo });
    } else {
      // No cached info (shouldn't happen from the mismatch view). Reset and
      // let the full flow run.
      loadAttemptedRef.current = false;
      setState({ kind: 'loading' });
    }
    // Keep the URL on the invite page - no-op in practice since we're
    // already here, but preserves the back-stack for test harnesses.
    void navigate(`/invite/${token}`);
  }, [performRecoverySignOut, state, token, navigate]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <div className={CARD_CLASSES}>
        {state.kind === 'loading' && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 rounded-full border-2 border-volt/30 border-t-volt animate-spin" />
          </div>
        )}

        {state.kind === 'error' && (
          <div
            className="text-center"
            role="alert"
            aria-labelledby="invite-error-heading"
          >
            <h1
              ref={blockingHeadingRef}
              id="invite-error-heading"
              tabIndex={-1}
              className="text-xl font-bold text-foreground mb-3 focus:outline-none"
            >
              {state.action === 'retry'
                ? "We couldn't open this invite"
                : "This invite link doesn't work"}
            </h1>
            <p className="text-muted-foreground mb-6">{state.message}</p>
            {state.action === 'retry' && (
              <button onClick={handleRetry} className={PRIMARY_BUTTON_CLASSES}>
                Try again
              </button>
            )}
          </div>
        )}

        {state.kind === 'uncertain' && (
          <div
            className="text-center"
            role="alert"
            aria-labelledby="invite-uncertain-heading"
          >
            <h1
              ref={blockingHeadingRef}
              id="invite-uncertain-heading"
              tabIndex={-1}
              className="text-xl font-bold text-foreground mb-3 focus:outline-none"
            >
              We couldn't confirm whether you joined
            </h1>
            <p className="text-muted-foreground mb-6">
              Check the invite status before trying again.
            </p>
            <button onClick={() => void handleCheckStatus()} className={PRIMARY_BUTTON_CLASSES}>
              Check status
            </button>
          </div>
        )}

        {state.kind === 'checkError' && (
          <div
            className="text-center"
            role="alert"
            aria-labelledby="invite-check-error-heading"
          >
            <h1
              ref={blockingHeadingRef}
              id="invite-check-error-heading"
              tabIndex={-1}
              className="text-xl font-bold text-foreground mb-3 focus:outline-none"
            >
              We couldn't check the invite status
            </h1>
            <p className="text-muted-foreground mb-6">
              Try opening the invite again.
            </p>
            <button onClick={() => void handleCheckStatus()} className={PRIMARY_BUTTON_CLASSES}>
              Check status again
            </button>
          </div>
        )}

        {state.kind === 'pendingAfterCheck' && (
          <div
            className="text-center"
            role="alert"
            aria-labelledby="invite-pending-check-heading"
          >
            <h1
              ref={blockingHeadingRef}
              id="invite-pending-check-heading"
              tabIndex={-1}
              className="text-xl font-bold text-foreground mb-3 focus:outline-none"
            >
              You haven't joined yet
            </h1>
            <p className="text-muted-foreground mb-6">
              The invite is still available. You can try accepting it again.
            </p>
            <button
              onClick={() => void handleTryAcceptAgain()}
              className={PRIMARY_BUTTON_CLASSES}
            >
              Try accepting again
            </button>
          </div>
        )}

        {state.kind === 'terminal' && (
          <div
            className="text-center"
            role="alert"
            aria-labelledby="invite-terminal-heading"
          >
            <h1
              ref={blockingHeadingRef}
              id="invite-terminal-heading"
              tabIndex={-1}
              className="text-xl font-bold text-foreground mb-3 focus:outline-none"
            >
              {terminalHeading(state.status)}
            </h1>
            {state.status === 'accepted' ? (
              <div className="flex flex-col gap-4">
                <p className="text-muted-foreground">
                  {session
                    ? 'Open holito to continue with this account.'
                    : 'Sign in with the account that accepted it.'}
                </p>
                {session ? (
                  <Link to="/admin" className={PRIMARY_BUTTON_CLASSES}>
                    Open holito
                  </Link>
                ) : (
                  <Link to={adminLoginHref} className={PRIMARY_BUTTON_CLASSES}>
                    Sign in
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">{terminalCopy(state.status)}</p>
            )}
          </div>
        )}

        {state.kind === 'reauth' && (
          <div
            className="text-center"
            role="alert"
            aria-labelledby="invite-reauth-heading"
          >
            <h1
              ref={blockingHeadingRef}
              id="invite-reauth-heading"
              tabIndex={-1}
              className="text-xl font-bold text-foreground mb-3 focus:outline-none"
            >
              We need to check your sign-in again
            </h1>
            <p className="text-muted-foreground mb-6">
              Sign in again to accept this invite.
            </p>
            {state.actionError && (
              <p className="text-danger text-sm mb-3">{state.actionError}</p>
            )}
            <button onClick={handleReauthenticate} className={PRIMARY_BUTTON_CLASSES}>
              Sign in again
            </button>
          </div>
        )}

        {state.kind === 'preLogin' && (
          <div>
            <h1 className="text-xl font-bold text-foreground mb-2">
              {state.info.inviter_name} invited you to join {state.info.org_name}
            </h1>
            <p className="text-muted-foreground mb-2">
              This invite expires on {formatExpiry(state.info.expires_at)}.
            </p>
            {/* Explicit account hint so the user signs in with the right address. */}
            <p className="text-sm text-muted-foreground mb-6">
              Sign in with <span className="font-medium text-foreground">{state.info.email_masked}</span> to accept.
            </p>
            <div className="flex flex-col gap-3">
              <Link to={loginHref} className={PRIMARY_BUTTON_CLASSES}>
                Sign in to accept
              </Link>
              <Link to="/" className={SECONDARY_BUTTON_CLASSES}>
                Go to home
              </Link>
            </div>
          </div>
        )}

        {state.kind === 'accepting' && (
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 rounded-full border-2 border-volt/30 border-t-volt animate-spin" />
            <p className="text-muted-foreground">Accepting...</p>
          </div>
        )}

        {state.kind === 'mismatch' && (
          <div role="alert" aria-labelledby="invite-mismatch-heading">
            <h1
              ref={blockingHeadingRef}
              id="invite-mismatch-heading"
              tabIndex={-1}
              className="text-xl font-bold text-foreground mb-3 focus:outline-none"
            >
              Use the account this invite was sent to
            </h1>
            <p className="text-muted-foreground mb-6">
              This invite was sent to{' '}
              <span className="font-medium text-foreground">{state.invitedEmailMasked}</span>.
              {state.signedInEmail ? (
                <>
                  {' '}You're signed in as{' '}
                  <span className="font-medium text-foreground">{state.signedInEmail}</span>.
                  {' '}Sign out, then sign in with the invited account.
                </>
              ) : (
                <> Sign in with the account that received this invite.</>
              )}
            </p>
            {state.actionError && (
              <p className="text-danger text-sm mb-3">{state.actionError}</p>
            )}
            <div className="flex flex-col gap-3">
              <button onClick={handleSignOutAndRetry} className={PRIMARY_BUTTON_CLASSES}>
                Sign out
              </button>
              <Link to="/" className={SECONDARY_BUTTON_CLASSES}>
                Go to home
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitePage;
